import {ref} from "vue";
import {appointmentApi} from "@/container";
import {AppointmentChangeResult, AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import {PatientSmsChoice, patientSmsOutcome, patientSmsRequest} from "@/agenda/lib/patientSms";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";

/**
 * The appointment changes the counter makes from a dialog — create, edit,
 * move, cancel — with the same feedback wherever the dialog is opened: what
 * became of the patient's text, and the server's error otherwise.
 *
 * Each call resolves true once the change and its `onSuccess` follow-up
 * (typically a refetch) are done; `saving` stays up until then, so the
 * dialog cannot be submitted twice meanwhile.
 */
const useAppointmentMutations = () => {
    const toastStore = useToastStore();
    const saving = ref(false);

    const perform = async (
        change: () => Promise<AppointmentChangeResult>,
        sms: PatientSmsChoice | null,
        onSuccess?: () => unknown
    ): Promise<boolean> => {
        saving.value = true;

        try {
            const {patientNotification} = await change();
            // Said as soon as the server answers, before the follow-up: a
            // failed refetch must not hide that the text already left.
            const outcome = patientSmsOutcome(sms, patientNotification);

            if (outcome) {
                toastStore.showToast(outcome);
            }

            await onSuccess?.();

            return true;
        } catch (error) {
            if (!isAuthorizationError(error)) {
                toastStore.showError({content: apiErrorMessage(error)});
            }

            return false;
        } finally {
            saving.value = false;
        }
    };

    const create = (
        organizationId: number,
        request: AppointmentCreateRequest,
        sms: PatientSmsChoice | null,
        onSuccess?: () => unknown
    ) => {
        return perform(
            () => appointmentApi().createAppointment(organizationId, {...request, ...patientSmsRequest(sms)}),
            sms,
            onSuccess
        );
    };

    const edit = (
        organizationId: number,
        appointmentId: number,
        request: AppointmentCreateRequest,
        sms: PatientSmsChoice | null,
        onSuccess?: () => unknown
    ) => {
        return perform(
            () => appointmentApi().editAppointment(organizationId, appointmentId, {...request, ...patientSmsRequest(sms)}),
            sms,
            onSuccess
        );
    };

    /** A new start, the length unchanged. */
    const reschedule = (
        appointmentId: number,
        startAt: string,
        sms: PatientSmsChoice | null,
        onSuccess?: () => unknown
    ) => {
        return perform(
            () => appointmentApi().rescheduleAppointment(appointmentId, {start_at: startAt, ...patientSmsRequest(sms)}),
            sms,
            onSuccess
        );
    };

    /** Soft: the appointment leaves the default views. */
    const cancel = (appointmentId: number, sms: PatientSmsChoice | null, onSuccess?: () => unknown) => {
        return perform(
            () => appointmentApi().cancelAppointment(appointmentId, patientSmsRequest(sms)),
            sms,
            onSuccess
        );
    };

    return {saving, create, edit, reschedule, cancel};
};

export default useAppointmentMutations;
